import * as Yup from 'yup'

const inputTypes = {}

inputTypes.checkbox = {
  APIToFormValues: modifiers => modifiers.map(o => ({ ...o })),
  getSchema: ({ minSelections, maxSelections }) => {
    let schema = Yup.array()
      .of(
        Yup.object().shape({
          selected: Yup.bool()
        })
      )
      .compact(a => !a.selected)
      .min(minSelections, 'min: ${min}') // eslint-disable-line no-template-curly-in-string

    if (maxSelections) schema = schema.max(maxSelections, 'max: ${max}') // eslint-disable-line no-template-curly-in-string
    return schema
  },
  formValuesToAPI: (modifiers, itemGroupGuid, quantity) =>
    modifiers.filter(m => m.selected).map(({ itemGuid }) => ({
      itemGuid,
      itemGroupGuid,
      modifierGroups: [],
      quantity
    }))
}

inputTypes.radio = {
  APIToFormValues: modifiers => {
    const found = modifiers.find(o => o.selected)
    return found ? found.itemGuid : ''
  },
  getSchema: () => Yup.string().required('required'),
  formValuesToAPI: (itemGuid, itemGroupGuid, quantity) => [
    {
      itemGuid,
      itemGroupGuid,
      modifierGroups: [],
      quantity
    }
  ]
}

inputTypes.fractionalQuantity = {
  defaultInitialValue: '1.00',
  getSchema: () => Yup.object().shape({
    fractionalQuantity: Yup.number().moreThan(0.24, 'Minimum is 0.25').required('Required')
  })
}

const isSingleSelect = (min, max) => min === 1 && max === 1

export const maxCharacters = 200

export const modifierFormHelpers = ({
  modifierGroups,
  itemGroupGuid,
  quantity = 1,
  usesFractionalQuantity = false,
  fractionalQuantity = {},
  specialInstructions = ''
}) => {
  const typeByGuid = {}
  const getType = guid => typeByGuid[guid]
  const initTypeByGuid = ({ guid, minSelections, maxSelections }) => {
    if (!typeByGuid[guid]) {
      const type = isSingleSelect(minSelections, maxSelections) ? 'radio' : 'checkbox'
      typeByGuid[guid] = type
    }
  }

  const initialValues = { modifiers: {}, specialInstructions, quantity }

  // add fractional quantity to validation schema if present
  const schemaShape = {}
  modifierGroups.forEach(group => {
    initTypeByGuid(group)
    const { guid, modifiers } = group
    const { APIToFormValues, getSchema } = inputTypes[getType(guid)]
    initialValues.modifiers[guid] = APIToFormValues(modifiers)
    schemaShape[guid] = getSchema(group)
  })

  let schema = Yup.object().shape({
    modifiers: Yup.object().shape(schemaShape),
    specialInstructions: Yup.string().max(maxCharacters, 'max ${max} characters') // eslint-disable-line no-template-curly-in-string
  })

  // account for fractional quantity
  if (usesFractionalQuantity) {
    initialValues.fractionalQuantity = fractionalQuantity.quantity || inputTypes.fractionalQuantity.defaultInitialValue
    schema = schema.concat(inputTypes.fractionalQuantity.getSchema())
  }

  const getModifierGroups = (values, quantity) => Object.entries(values.modifiers).reduce(
    (acc, [guid, groups]) =>
      acc.concat([{
        guid,
        modifiers: inputTypes[getType(guid)].formValuesToAPI(groups, itemGroupGuid, quantity)
      }]),
    []
  )

  const getComputedPrice = (values, quantity) => {
    const selectedGuids = Object.entries(values.modifiers)
      .reduce((acc, [guid, groups]) => acc.concat(inputTypes[getType(guid)].formValuesToAPI(groups, itemGroupGuid, quantity)), [])
      .reduce((acc, o) => [...acc, o.itemGuid], [])

    const guidToPriceMap = modifierGroups.reduce((acc, g) => {
      const mods = g.modifiers.reduce((ac, m) => {
        ac[m.itemGuid] = m.price
        return ac
      }, {})
      return {...acc, ...mods}
    }, {})

    return selectedGuids.reduce((acc, o) => {
      const val = guidToPriceMap[o] || 0
      return val + acc
    }, 0)
  }

  return {
    initialValues,
    schema,
    getModifierGroups,
    getComputedPrice,
    maxCharacters
  }
}
